import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getTestimonials,
  approveTestimonial,
  disapproveTestimonial,
} from "../../api/testimonialService";
import { getErrorMessage } from "../../utils/errorHandler";
import Pagination from "../Pagination/Pagination";

export default function TestimonialList() {
  const navigate = useNavigate();

  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const fetchTestimonials = async (page = 1) => {
    setLoading(true);

    try {
      const res = await getTestimonials(page, itemsPerPage);

      setTestimonials(res?.data?.testimonials || []);
      setTotalItems(res?.data?.totalItems || 0);
      setTotalPages(res?.data?.totalPages || 1);
    } catch (error) {
      alert(getErrorMessage(error));
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials(currentPage);
  }, [currentPage]);

  const handleApprove = async (id) => {
    try {
      await approveTestimonial(id);

      alert("Testimonial approved successfully");

      fetchTestimonials(currentPage);
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  const handleDisapprove = async (id) => {
    try {
      await disapproveTestimonial(id);

      alert("Testimonial disapproved successfully");

      fetchTestimonials(currentPage);
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  return (
    <div className="container-fluid p-4 np-testimonials-page">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Testimonials List</h5>
      </div>

      {/* Loading */}
      {loading && <p>Loading testimonials...</p>}

      {/* Empty State */}
      {!loading && testimonials.length === 0 && <p className="text-muted">No Testimonials Found</p>}

      {/* Table */}
      {!loading && testimonials.length > 0 && (
        <div className="card p-3">
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th className="text-dark fw-bold">#</th>
                  <th className="text-dark fw-bold">Customer Name</th>
                  <th className="text-dark fw-bold">Location</th>
                  <th className="text-dark fw-bold">Rating</th>
                  <th className="text-dark fw-bold">Review</th>
                  <th className="text-dark fw-bold">Status</th>
                  <th className="text-dark fw-bold">Actions</th>
                </tr>
              </thead>

              <tbody>
                {testimonials.map((testimonial, index) => (
                  <tr key={testimonial._id}>
                    {/* # */}
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>

                    {/* Customer Name */}
                    <td>{testimonial.name || "-"}</td>

                    {/* Location */}
                    <td>
                      {testimonial.city || "-"}
                      {testimonial.state ? `, ${testimonial.state}` : ""}
                    </td>

                    {/* Rating */}
                    <td>
                      <div className="text-nowrap">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <i
                            key={star}
                            className={`fa ${
                              star <= testimonial.rating
                                ? "fa-star text-warning"
                                : "fa-star-o text-muted"
                            }`}
                          />
                        ))}
                      </div>
                    </td>

                    {/* Review */}
                    <td style={{ minWidth: "250px", maxWidth: "350px" }}>
                      <div
                        style={{
                          maxHeight: "80px",
                          overflow: "hidden",
                        }}
                      >
                        {testimonial.review || "-"}
                      </div>
                    </td>

                    {/* Status */}
                    <td>
                      <span
                        className={`badge ${
                          testimonial.status === "APPROVED"
                            ? "bg-success"
                            : testimonial.status === "DISAPPROVED"
                              ? "bg-danger"
                              : "bg-warning text-dark"
                        }`}
                      >
                        {testimonial.status || "PENDING"}
                      </span>
                    </td>

                    {/* Actions */}
                    <td>
                      {testimonial.status !== "APPROVED" &&
                        testimonial.status !== "DISAPPROVED" && (
                          <div className="d-flex justify-content-center gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-success"
                              onClick={() => handleApprove(testimonial._id)}
                            >
                              Approve
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDisapprove(testimonial._id)}
                            >
                              Disapprove
                            </button>
                          </div>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalItems > 0 && (
        <Pagination
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      )}
    </div>
  );
}
